const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

const PUBLIC_DOMAIN_CONFIGS = [
  {
    siteHosts: ['dobidobi.ai', 'www.dobidobi.ai'],
    siteUrl: 'https://www.dobidobi.ai',
    apiBaseUrl: 'https://api.dobidobi.ai/api',
    cookieDomain: '.dobidobi.ai',
  },
  {
    siteHosts: ['octoailab.com', 'www.octoailab.com'],
    siteUrl: 'https://www.octoailab.com',
    apiBaseUrl: 'https://api.octoailab.com/api',
    cookieDomain: '.octoailab.com',
  },
] as const;

const PRIMARY_DOMAIN_CONFIG = PUBLIC_DOMAIN_CONFIGS[0];

function normalizeHostname(hostname?: string | null): string {
  return (hostname || '').trim().toLowerCase().replace(/\.+$/, '');
}

function getBrowserHostname(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return normalizeHostname(window.location.hostname);
}

function getConfiguredSiteUrl(): string | null {
  const envUrl = process.env.NEXT_PUBLIC_SITE_URL?.trim();
  return envUrl || null;
}

function getConfiguredApiBaseUrl(): string | null {
  const envUrl = process.env.NEXT_PUBLIC_API_URL?.trim();
  return envUrl || null;
}

function alignLocalApiHostname(configuredApiBaseUrl: string, browserHostname: string): string {
  try {
    const configuredUrl = new URL(configuredApiBaseUrl);
    if (isLocalHostname(configuredUrl.hostname) && isLocalHostname(browserHostname)) {
      configuredUrl.hostname = browserHostname;
      return configuredUrl.toString().replace(/\/$/, '');
    }
  } catch {
    // Preserve the configured value so existing validation and request errors stay visible.
  }

  return configuredApiBaseUrl;
}

function findPublicDomainConfig(hostname?: string | null) {
  const normalizedHostname = normalizeHostname(hostname);
  if (!normalizedHostname) {
    return null;
  }

  return PUBLIC_DOMAIN_CONFIGS.find((config) =>
    config.siteHosts.some((siteHost) => siteHost === normalizedHostname),
  ) ?? null;
}

export function isLocalHostname(hostname: string): boolean {
  return LOCAL_HOSTNAMES.has(normalizeHostname(hostname));
}

export function resolveApiBaseUrl(hostname?: string): string {
  const normalizedHostname = normalizeHostname(hostname ?? getBrowserHostname());

  if (normalizedHostname) {
    if (isLocalHostname(normalizedHostname)) {
      if (typeof window !== 'undefined'
        && process.env.NODE_ENV === 'development'
        && process.env.NEXT_PUBLIC_LOCAL_API_PROXY === 'true') {
        return '/api';
      }
      const configuredApiBaseUrl = getConfiguredApiBaseUrl();
      return configuredApiBaseUrl
        ? alignLocalApiHostname(configuredApiBaseUrl, normalizedHostname)
        : 'http://localhost:8000/api';
    }

    const publicDomainConfig = findPublicDomainConfig(normalizedHostname);
    if (publicDomainConfig) {
      return publicDomainConfig.apiBaseUrl;
    }
  }

  return getConfiguredApiBaseUrl() || PRIMARY_DOMAIN_CONFIG.apiBaseUrl;
}

export function resolveBackendOrigin(hostname?: string): string {
  return resolveApiBaseUrl(hostname).replace(/\/api\/?$/, '');
}

export function resolvePublicSiteUrl(hostname?: string): string {
  const normalizedHostname = normalizeHostname(hostname ?? getBrowserHostname());
  const publicDomainConfig = findPublicDomainConfig(normalizedHostname);
  if (publicDomainConfig) {
    return publicDomainConfig.siteUrl;
  }

  return getConfiguredSiteUrl() || PRIMARY_DOMAIN_CONFIG.siteUrl;
}

export function resolveCookieDomain(hostname?: string): string {
  const normalizedHostname = normalizeHostname(hostname ?? getBrowserHostname());
  if (!normalizedHostname || isLocalHostname(normalizedHostname)) {
    return '';
  }

  const publicDomainConfig = findPublicDomainConfig(normalizedHostname);
  return publicDomainConfig?.cookieDomain || '';
}

export function resolveCookieDomainAttribute(hostname?: string): string {
  const cookieDomain = resolveCookieDomain(hostname);
  return cookieDomain ? `; domain=${cookieDomain}` : '';
}

export const API_BASE_URL = resolveApiBaseUrl();
